import { computed, effect, inject, Injectable, OnDestroy, signal, untracked } from '@angular/core';
import { ClientSessionService, UiSession } from '@brighthub/uisession';
import { MessageService } from 'primeng/api';
import {
  applyFilter,
  applyScoring,
  buildSections,
  bucketFromScore,
  scoreRow,
} from './client-queue-priority.engine';
import { ClientQueueEnrichmentService } from './client-queue-enrichment.service';
import {
  EnrichedQueueRow,
  QueueBucket,
  QueueFilter,
  QueueSection,
} from './client-queue.model';

@Injectable()
export class ClientQueueStateService implements OnDestroy {
  private readonly _sessionService = inject(ClientSessionService);
  private readonly _enrichment = inject(ClientQueueEnrichmentService);
  private readonly _messageService = inject(MessageService);

  // ---------------------------------------------------------------------------
  // Filter state
  // ---------------------------------------------------------------------------

  readonly activeFilter = signal<QueueFilter>('all');

  // ---------------------------------------------------------------------------
  // Snooze map — sessionId → timestamp when snooze expires
  // ---------------------------------------------------------------------------

  private readonly _snoozedUntil = signal<Record<string, number>>({});

  // ---------------------------------------------------------------------------
  // Stable order map — sessionId → stable index assigned on first load
  // Prevents rows from jumping position on minor score changes.
  // ---------------------------------------------------------------------------

  private _stableOrder = new Map<string, number>();
  private _stableOrderInitialized = false;

  // ---------------------------------------------------------------------------
  // Enriched rows (recomputed when sessions change)
  // ---------------------------------------------------------------------------

  private readonly _enrichedRows = computed<EnrichedQueueRow[]>(() => {
    const sessions = this._sessionService.sessions();
    const snoozed = this._snoozedUntil();
    const now = Date.now();

    // Filter out snoozed sessions
    const activeSessions = sessions.filter((s) => {
      const exp = snoozed[s.uiSessionId];
      return !exp || exp <= now;
    });

    // Enrich with mock metadata
    const enriched = this._enrichment.enrich(activeSessions);

    // Apply scoring
    enriched.forEach((row) => applyScoring(row));

    // Assign stable indices — only set once per session ID
    if (!this._stableOrderInitialized && enriched.length > 0) {
      enriched.forEach((row, i) => {
        this._stableOrder.set(row.session.uiSessionId, i);
      });
      this._stableOrderInitialized = true;
    }

    // Apply stable index to each row (new sessions get appended at the end)
    let maxIdx = this._stableOrder.size;
    enriched.forEach((row) => {
      const id = row.session.uiSessionId;
      if (!this._stableOrder.has(id)) {
        this._stableOrder.set(id, maxIdx++);
      }
      row.stableIndex = this._stableOrder.get(id)!;
    });

    return enriched;
  });

  // ---------------------------------------------------------------------------
  // Sections (Now / Next / Later) — filtered and stably ordered
  // ---------------------------------------------------------------------------

  readonly sections = computed<QueueSection[]>(() => {
    const rows = this._enrichedRows();
    const filter = this.activeFilter();
    const filtered = applyFilter(rows, filter);
    return buildSections(filtered);
  });

  // ---------------------------------------------------------------------------
  // Convenience: total unsnoozed active rows
  // ---------------------------------------------------------------------------

  readonly totalCount = computed(() =>
    this._enrichedRows().length
  );

  readonly nowCount = computed(() =>
    this.sections().find((s) => s.bucket === 'now')?.rows.length ?? 0
  );

  // ---------------------------------------------------------------------------
  // Snooze expiry timer — re-evaluates snoozed rows every 30 seconds
  // ---------------------------------------------------------------------------

  private _snoozeTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    // Kick off snooze expiry polling
    this._snoozeTimer = setInterval(() => {
      const snoozed = this._snoozedUntil();
      const now = Date.now();
      const expired = Object.keys(snoozed).filter((id) => snoozed[id] <= now);
      if (expired.length > 0) {
        this._snoozedUntil.update((prev) => {
          const next = { ...prev };
          expired.forEach((id) => delete next[id]);
          return next;
        });
      }
    }, 30_000);
  }

  ngOnDestroy(): void {
    if (this._snoozeTimer !== null) {
      clearInterval(this._snoozeTimer);
    }
  }

  // ---------------------------------------------------------------------------
  // Public actions
  // ---------------------------------------------------------------------------

  setFilter(filter: QueueFilter): void {
    this.activeFilter.set(filter);
  }

  /**
   * Snooze a session for 15 minutes — removes it from Now/Next temporarily.
   * Demo mode: shows a toast; no API call.
   */
  snooze(row: EnrichedQueueRow): void {
    const expiresAt = Date.now() + 15 * 60 * 1000;
    this._snoozedUntil.update((prev) => ({
      ...prev,
      [row.session.uiSessionId]: expiresAt,
    }));
    const name = `${row.session.subjectData.firstName} ${row.session.subjectData.lastName}`;
    this._messageService.add({
      severity: 'info',
      summary: 'Snoozed',
      detail: `${name} snoozed for 15 minutes.`,
      life: 4000,
    });
  }

  /**
   * Assign to me — demo mode: shows a success toast.
   */
  assignToMe(row: EnrichedQueueRow): void {
    const name = `${row.session.subjectData.firstName} ${row.session.subjectData.lastName}`;
    this._messageService.add({
      severity: 'success',
      summary: 'Assigned',
      detail: `${name} has been assigned to you.`,
      life: 4000,
    });
  }

  /**
   * Archive — delegates to the real ClientSessionService.
   */
  archive(row: EnrichedQueueRow): void {
    this._sessionService.archiveClientSession(row.session.uiSessionId);
  }

  /**
   * Reset stable order (e.g. after a full reload). Useful in tests.
   */
  resetStableOrder(): void {
    this._stableOrder.clear();
    this._stableOrderInitialized = false;
  }
}
